/* Performance — app product health. Downloads · DAU/MAU · crash-free · D7
   retention · funnel · stability sparkline · retention curve · version adoption ·
   open issues DS Table with send-to-crew. App Store Connect + Sentry are [I DO]
   (coming soon); UI complete. De-nested from a single Widget. */
import { Sparkline, WidgetBody } from '../../ds/mc-kit';
import { Button, Table } from '../../ds';
import { useContract } from '../../lib/api';
import { useOps } from '../ops';
import { Panel, Kicker } from '../ui';
import { integrationFor } from '../../lib/flags';
import { derivePerf } from '../../lib/selectors';

export function Performance() {
  const perf = useContract('perf', { pollMs: 60_000 });
  const apps = useContract('apps', { pollMs: 0 });
  const { pushToast, appendAudit } = useOps();
  const connect = integrationFor('perf')?.connect;

  const P = perf.data;
  const d = P ? derivePerf(P) : null;
  const app = apps.data?.[0];

  const sendToCrew = (title: string, agent: string) => {
    appendAudit({ actor: 'Cassidy', action: 'Assigned issue', target: title + (agent ? ` → ${agent}` : '') });
    pushToast('success', agent ? `Sent to ${agent}` : 'Sent to crew', title);
  };

  return (
    <div data-screen-label="Performance" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === APP SWITCHER HEADER === */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button onClick={() => {/* app switch */}} aria-label="Switch app"
          style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '7px 12px 7px 7px', cursor: 'pointer' }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 18, background: 'var(--surface2)', border: '1px solid var(--border)', flex: 'none' }}>
            {app?.glyph ?? '🥾'}
          </span>
          <span style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{app?.name ?? 'TrailQuest'}</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{app?.tag ?? 'iOS · App Store'}</span>
          </span>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2 }} aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4" /></svg>
          App Store Connect · Sentry
        </span>

        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', animation: 'ct-pulse 2s ease-in-out infinite' }} />
          live
        </span>
      </div>

      {/* === 4 CUSTOM STAT CARDS === */}
      <div className="mc-auto mc-auto-md">
        {/* Downloads */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Downloads · 30d</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{P?.downloads.delta ?? '—'}</span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 27, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{P?.downloads.total ?? '—'}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>first-time installs</div>
        </div>
        {/* Active users */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Active users</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{P?.dau.delta ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 27, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{P?.dau.value ?? '—'}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)' }}>DAU</span>
          </div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{P?.dau.mau ?? '—'} MAU · {P?.dau.stickiness ?? '—'} sticky</div>
        </div>
        {/* Crash-free */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Crash-free</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{P?.crashFree.delta ?? '—'}</span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 27, letterSpacing: '-0.02em', color: 'var(--accent-ink)' }}>{P?.crashFree.value ?? '—'}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{P?.crashFree.detail ?? '—'}</div>
        </div>
        {/* D7 retention */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>D7 retention</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>cohort</span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 27, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{P ? `${P.retention.d7}%` : '—'}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>
            {P ? `${P.retention.d1}% D1 · ${P.retention.d30}% D30` : '—'}
          </div>
        </div>
      </div>

      {/* === DOWNLOADS sparkline (1.5fr) + FUNNEL (1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1.5fr 1fr' } as React.CSSProperties}>
        <section aria-label="Downloads · 30d" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
            <Kicker>// downloads · 30d</Kicker>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>first-time installs</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 32, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{P?.downloads.total ?? '—'}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--accent-ink)' }}>{P?.downloads.delta}</span>
          </div>
          <WidgetBody state={perf.state} minHeight={140} rows={3}
            emptyLabel="No installs yet" errorLabel="Analytics offline" errorSub="Couldn't reach App Store Connect." onRetry={perf.refetch}>
            {P && <Sparkline data={P.downloads.spark} height={140} tone="cyan" />}
          </WidgetBody>
        </section>

        <Panel title="// app store funnel" label="App Store funnel">
          {(d?.funnel ?? []).map((f) => (
            <div key={f.label} style={{ marginBottom: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                <span style={{ fontSize: 12.5, color: 'var(--ink)' }}>{f.label}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink)' }}>{f.value}</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${f.pct}%`, borderRadius: 999, background: f.barColor }} />
              </div>
            </div>
          ))}
          {P && (
            <div style={{ marginTop: 15, paddingTop: 13, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>impression → install</span>
              <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 16, color: 'var(--accent-ink)' }}>{P.conversion}</span>
            </div>
          )}
        </Panel>
      </div>

      {/* === 3-COL: stability | retention | version adoption === */}
      <div className="mc-auto mc-auto-md">
        {/* Stability with 26px crash-free readout */}
        <Panel title="// stability" label="Stability">
          {P && (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginBottom: 3 }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 26, letterSpacing: '-0.02em', color: 'var(--accent-ink)' }}>{P.crashFree.value}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{P.crashFree.delta}</span>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>crash-free sessions</div>
              <Sparkline data={P.crashSpark} height={44} tone="cyan" />
            </>
          )}
        </Panel>

        <Panel title="// retention" label="Retention">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(d?.retention ?? []).map((r) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 11 }}>
                <span style={{ width: 44, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--muted)', flex: 'none' }}>{r.label}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${r.pct}%`, borderRadius: 999, background: r.color }} />
                </div>
                <span style={{ width: 34, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink)', flex: 'none' }}>{r.pct}%</span>
              </div>
            ))}
            <div style={{ marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>share of new users still active</div>
          </div>
        </Panel>

        <Panel title="// version adoption" label="Version adoption">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(d?.versions ?? []).map((v) => (
              <div key={v.v} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 11 }}>
                <span style={{ width: 58, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--ink)', flex: 'none' }}>{v.v}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${v.pct}%`, borderRadius: 999, background: v.color }} />
                </div>
                <span style={{ width: 34, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)', flex: 'none' }}>{v.pct}%</span>
              </div>
            ))}
            <div style={{ marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>78% on the latest build</div>
          </div>
        </Panel>
      </div>

      {/* === OPEN ISSUES (DS Table) === */}
      <section aria-label="Open issues" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
          <Kicker>// open issues</Kicker>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>Sentry · TrailQuest iOS</span>
        </div>
        <WidgetBody state={perf.state} minHeight={200} rows={4}
          emptyLabel="No open issues" emptySub="Crash-free and quiet." errorLabel="Sentry unavailable" errorSub="Couldn't reach the issues API." onRetry={perf.refetch}>
          {d && (() => {
            const issueColumns = [
              { key: 'issue', label: 'issue' },
              { key: 'severity', label: 'severity' },
              { key: 'affected', label: 'affected' },
              { key: 'status', label: 'status' },
              { key: 'owner', label: 'owner', align: 'right' as const },
            ];
            const issueRows = d.issues.map((i) => ({
              key: i.id,
              issue: (
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink)' }}>{i.title}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>v{i.ver}</div>
                </div>
              ),
              severity: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, background: i.sevSoft, border: `1px solid ${i.sevLine}`, color: i.sevColor, fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: i.sevColor }} />
                  {i.sev}
                </span>
              ),
              affected: i.affected,
              status: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: i.stInk }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: i.stColor, boxShadow: i.stPulse ? `0 0 7px ${i.stColor}` : undefined, animation: i.stPulse ? 'ct-pulse 2s ease-in-out infinite' : undefined }} />
                  {i.status}
                </span>
              ),
              owner: i.hasAgent ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 6px', borderRadius: 999, background: 'var(--surface2)', border: '1px solid var(--border)', fontFamily: 'var(--font-heading)', fontWeight: 500, fontSize: 12, color: 'var(--ink)' }}>
                  <span style={{ fontSize: 13 }}>{i.glyph}</span>{i.agent}
                </span>
              ) : i.needsOwner ? (
                <Button variant="secondary" size="sm" onClick={() => sendToCrew(i.title, i.agent)}>→ send to crew</Button>
              ) : null,
            }));
            return <div className="mc-table-wrap"><Table columns={issueColumns} rows={issueRows} /></div>;
          })()}
        </WidgetBody>

        {connect && perf.isPlaceholder && (
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
            {connect} · showing sample data
          </div>
        )}
      </section>
    </div>
  );
}
